import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { createContext, useContext, useState, ReactNode } from "react";
import { LoadingPage } from "~/components/loading";
import { Config } from "~/constants/configuration_provider";

export type Region = {
  name: string;
  url: string;
};

const parseRegions = (regionString: string | null): Region[] => {
  let parsedRegions: Region[] = [];

  if (!regionString) {
    console.warn("No regions string provided to parseRegions");
    return parsedRegions;
  }

  const rows = regionString.toString().split("|");
  rows.forEach((row) => {
    const parts = row.split(",");
    if (parts.length === 2) {
      parsedRegions.push({ name: parts[0], url: parts[1] });
    } else {
      console.warn(
        `Failed to parse region row: "${row}". Expected format: "name,url"`,
      );
    }
  });
  return parsedRegions;
};

function parseRegionsFromConfig(): Region[] {
  const regionString = Config.VITE_REGIONS;
  if (!regionString) {
    console.warn("VITE_REGIONS is not set in config");
  }
  const parsedRegions = parseRegions(regionString);
  if (parsedRegions.length === 0) {
    console.warn(
      "No valid regions found in config, will use default US region",
    );
  }
  return parsedRegions;
}

const DEFAULT_REGION = parseRegionsFromConfig()[0] || {
  name: "US",
  url: "https://us.api.mondoo.com", // hardcoded US fallback
};

/**
 * Resolve the active region NAME from its persisted source — the `?region=`
 * search param, then `localStorage["regionName"]`, falling back to the first
 * configured region. This is the same precedence RegionsProvider uses for its
 * initial selection, exposed as a free function so code that runs ABOVE the
 * provider (e.g. the incident banner, mounted in the auth provider) can read
 * the region without the context. Returns undefined only when no regions are
 * configured (single-region / no VITE_REGIONS), where region scoping is moot.
 */
export function resolveActiveRegionName(): string | undefined {
  const regions = parseRegionsFromConfig();
  const requested =
    new URLSearchParams(location.search).get("region") ||
    (typeof localStorage !== "undefined"
      ? localStorage.getItem("regionName")
      : null);
  const found = regions.find((region) => region.name === requested);
  return found?.name ?? regions[0]?.name;
}

interface RegionsContextType {
  regions: Region[];
  selectedRegion: Region;
  findRegion: (name: string) => Region | undefined;
  updateSelectedRegion: (region: Region) => void;
  selectedApiEndpoint: string;
  selectedGraphqlEndpoint: string;
}

const RegionsContext = createContext<RegionsContextType | undefined>(undefined);

export function RegionsProvider({ children }: { children: ReactNode }) {
  const searchParams = new URLSearchParams(location.search);
  const initialRegions = parseRegionsFromConfig();
  const [regions, _setRegions] = useState<Region[]>(
    initialRegions.length > 0 ? initialRegions : [DEFAULT_REGION],
  );
  const [selectedRegion, setSelectedRegion] = useState<Region>(() => {
    const regionName =
      searchParams.get("region") || localStorage.getItem("regionName");
    const found = initialRegions.find((region) => region.name === regionName);
    return found || initialRegions[0] || DEFAULT_REGION;
  });
  const [isRegionChanging, setIsRegionChanging] = useState(false);

  const findRegion = (name: string): Region | undefined => {
    return regions.find((region) => region.name === name);
  };

  const updateSelectedRegion = async (region: Region) => {
    if (region.name === selectedRegion?.name) return;

    try {
      setIsRegionChanging(true);
      localStorage.setItem("regionName", region.name);

      // Clear any existing data that might be region-specific and don't refetch active queries
      if (window.__APOLLO_CLIENT__) {
        await window.__APOLLO_CLIENT__.clearStore();
      }

      setSelectedRegion(region);
    } finally {
      setIsRegionChanging(false);
    }
  };

  const value = {
    regions,
    selectedRegion,
    findRegion,
    updateSelectedRegion,
    selectedApiEndpoint: selectedRegion.url,
    selectedGraphqlEndpoint: selectedRegion.url + "/query",
  };

  return (
    <RegionsContext.Provider value={value}>
      {isRegionChanging ? (
        <RegionChangeLoader regionName={selectedRegion.name} />
      ) : (
        children
      )}
    </RegionsContext.Provider>
  );
}

export function useRegions() {
  const context = useContext(RegionsContext);
  if (context === undefined) {
    throw new Error("useRegions must be used within a RegionsProvider");
  }
  return context;
}

const RegionChangeLoader = ({ regionName }: { regionName: string }) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
      }}
    >
      <Stack direction="row" alignItems="center" gap={2}>
        <CircularProgress color="secondary" />
        <Typography>Switching to {regionName} region...</Typography>
      </Stack>
    </Box>
  );
};
